(function () {
  "use strict";

  var productInventory = new Map();
  var activeVariantId = null;

  function number(value, fallback) {
    var parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function variantId(value) {
    return String(value == null ? "" : value).trim();
  }

  function isCapped(record) {
    return Boolean(record && record.inventoryManagement && record.inventoryPolicy === "deny");
  }

  function maxAdditional(record) {
    if (!isCapped(record)) return null;
    return Math.max(0, number(record.inventoryQuantity, 0) - number(record.cartQuantity, 0));
  }

  function setMessage(scope, message) {
    if (!scope) return;
    var target = scope.querySelector("[data-inventory-message]");
    if (target) target.textContent = message || "";
  }

  function setButton(button, disabled, text) {
    if (!button) return;
    if (!button.dataset.inventoryDefaultText) {
      button.dataset.inventoryDefaultText = button.textContent.trim() || "Add to Cart";
    }
    button.disabled = Boolean(disabled);
    button.setAttribute("aria-disabled", String(Boolean(disabled)));
    button.textContent = text || button.dataset.inventoryDefaultText;
  }

  function productLimitMessage(record, max) {
    if (record.available === false || (max === 0 && number(record.inventoryQuantity, 0) <= 0)) return "Sold out.";
    return max === 0 && number(record.cartQuantity, 0) > 0 ? "All available copies of this variant are already in your cart." : "";
  }

  function applyProductVariant(record) {
    if (!record) return;
    activeVariantId = variantId(record.id);
    var cappedMax = maxAdditional(record);
    var unavailable = record.available === false || (cappedMax !== null && cappedMax <= 0);
    var message = productLimitMessage(record, cappedMax);

    document.querySelectorAll("[data-inventory-product-form]").forEach(function (form) {
      var idInput = form.querySelector('input[name="id"]');
      if (idInput && variantId(idInput.value) !== activeVariantId) return;

      var quantityInput = form.querySelector("[data-inventory-quantity-input]");
      if (quantityInput) {
        if (cappedMax === null) {
          quantityInput.removeAttribute("max");
          quantityInput.removeAttribute("data-inventory-max");
          quantityInput.disabled = false;
        } else {
          quantityInput.max = String(cappedMax);
          quantityInput.dataset.inventoryMax = String(cappedMax);
          quantityInput.disabled = cappedMax <= 0;
          if (number(quantityInput.value, 1) > cappedMax && cappedMax > 0) {
            quantityInput.value = String(cappedMax);
          }
        }
      }

      var submit = form.querySelector("[data-inventory-submit]");
      var text = record.available === false ? "Sold out" : cappedMax === 0 ? (form.classList.contains("darkstone-mobile-purchase-bar") ? "In cart" : "All available copies are in your cart") : null;
      setButton(submit, unavailable, text);
      setMessage(form, message);
    });
  }

  function updateProductVariant(variant) {
    var id = variantId(variant && variant.id);
    var record = productInventory.get(id);
    if (!record) return;
    if (variant && typeof variant.available === "boolean") record.available = variant.available;
    applyProductVariant(record);
  }

  function parseProductInventory() {
    var source = document.getElementById("ProductInventoryJson");
    if (!source) return;
    try {
      var records = JSON.parse(source.textContent || "[]");
      records.forEach(function (record) {
        record.id = variantId(record.id);
        record.inventoryQuantity = number(record.inventoryQuantity, 0);
        record.cartQuantity = number(record.cartQuantity, 0);
        productInventory.set(record.id, record);
      });
      var selected = document.getElementById("selected-variant-id");
      if (selected) updateProductVariant({ id: selected.value });
    } catch (error) {
      console.error("[Darkstone inventory] Product inventory data could not be parsed.", error);
    }
  }

  function quickAddState(form) {
    if (!form || !form.hasAttribute("data-inventory-max-total")) return null;
    return {
      maxTotal: Math.max(0, number(form.dataset.inventoryMaxTotal, 0)),
      cartQuantity: Math.max(0, number(form.dataset.inventoryCartQuantity, 0)),
      addQuantity: Math.max(1, number(form.querySelector('input[name="quantity"]')?.value, 1)),
    };
  }

  function renderQuickAdd(form) {
    var state = quickAddState(form);
    if (!state) return;
    var reached = state.cartQuantity >= state.maxTotal;
    var button = form.querySelector('button[type="submit"]');
    setButton(button, reached, reached ? "In Cart" : null);
    setMessage(form, reached ? "All available copies are in your cart." : "");
  }

  function guardQuickAdd(event, form) {
    var state = quickAddState(form);
    if (!state) return false;
    if (state.cartQuantity + state.addQuantity <= state.maxTotal) return false;
    event.preventDefault();
    event.stopImmediatePropagation();
    renderQuickAdd(form);
    return true;
  }

  async function addVariant(variant, quantity) {
    var response = await fetch(window.routes?.cart_add_url || "/cart/add.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ items: [{ id: variant, quantity: quantity }] }),
    });
    var payload = await response.json().catch(function () { return null; });
    if (!response.ok) {
      var failure = new Error(payload?.description || payload?.message || "Inventory changed. Refresh and try again.");
      failure.isInventoryError = response.status === 422;
      throw failure;
    }
    var item = Array.isArray(payload?.items) ? payload.items[0] : payload;
    document.dispatchEvent(new CustomEvent("darkstone:cart-updated", {
      detail: { variantId: item?.variant_id || item?.id || variant, quantityDelta: quantity },
    }));
    document.dispatchEvent(new CustomEvent("darkstone:refresh-header-cart"));
    return item;
  }

  async function submitStandaloneQuickAdd(event, form) {
    if (guardQuickAdd(event, form)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    var button = form.querySelector('button[type="submit"]');
    var original = button?.textContent || "Add to cart";
    if (button) { button.disabled = true; button.textContent = "Adding…"; }
    try {
      await addVariant(form.querySelector('input[name="id"]')?.value, Math.max(1, number(form.querySelector('input[name="quantity"]')?.value, 1)));
      renderQuickAdd(form);
    } catch (error) {
      setMessage(form, error.message);
      if (button && error?.isInventoryError) {
        button.dataset.inventoryBlocked = "true";
        button.textContent = "Refresh stock";
      }
    } finally {
      if (button && button.dataset.inventoryBlocked !== "true") {
        button.disabled = false;
        button.textContent = original;
        renderQuickAdd(form);
      }
    }
  }

  async function submitProductForm(event, form) {
    var id = variantId(form.querySelector('input[name="id"]')?.value);
    var record = productInventory.get(id);
    var max = maxAdditional(record);
    var requested = Math.max(1, number(form.querySelector('input[name="quantity"]')?.value, 1));
    event.preventDefault();
    event.stopImmediatePropagation();
    if (record && record.available === false) {
      setMessage(form, "Sold out.");
      return;
    }
    if (max !== null && requested > max) {
      setMessage(form, max <= 0 ? productLimitMessage(record, max) : "Only " + max + " more currently available.");
      return;
    }
    var button = form.querySelector("[data-inventory-submit]");
    var original = button?.textContent || "Add to Cart";
    if (button) { button.disabled = true; button.textContent = "Adding…"; }
    try {
      await addVariant(id, requested);
      window.location.href = window.routes?.cart_url || "/cart";
    } catch (error) {
      setMessage(form, error.message);
      if (button) {
        button.disabled = Boolean(error?.isInventoryError);
        button.textContent = error?.isInventoryError ? "Refresh stock" : original;
      }
    }
  }

  function v1QuickAddState(button) {
    if (!button.hasAttribute("data-inventory-max-total")) return null;
    return {
      maxTotal: Math.max(0, number(button.dataset.inventoryMaxTotal, 0)),
      cartQuantity: Math.max(0, number(button.dataset.inventoryCartQuantity, 0)),
    };
  }

  function renderV1QuickAdd(button) {
    var state = v1QuickAddState(button);
    if (!state) return;
    var reached = state.cartQuantity >= state.maxTotal;
    button.disabled = reached;
    button.setAttribute("aria-disabled", String(reached));
    button.textContent = reached ? "✓" : "+";
    setMessage(button.closest(".v1-product-card"), reached ? "All available copies are in your cart." : "");
  }

  async function submitV1QuickAdd(event, button) {
    event.preventDefault();
    event.stopImmediatePropagation();
    var state = v1QuickAddState(button);
    if (state && state.cartQuantity >= state.maxTotal) {
      renderV1QuickAdd(button);
      return;
    }
    button.disabled = true;
    button.textContent = "…";
    try {
      await addVariant(button.dataset.productId, 1);
      renderV1QuickAdd(button);
    } catch (error) {
      setMessage(button.closest(".v1-product-card"), error.message);
      button.dataset.inventoryBlocked = "true";
      button.textContent = "!";
    } finally {
      if (button.dataset.inventoryBlocked !== "true") {
        if (v1QuickAddState(button)) {
          renderV1QuickAdd(button);
        } else {
          button.disabled = false;
          button.textContent = "+";
        }
      }
    }
  }

  function clampCartInput(input) {
    if (!input || !input.hasAttribute("data-inventory-max")) return false;
    var max = Math.max(0, number(input.dataset.inventoryMax, 0));
    var requested = Math.max(0, number(input.value, 0));
    if (requested <= max) return false;
    input.value = String(max);
    var productForm = input.closest("[data-inventory-product-form]");
    var scope = input.closest("[data-inventory-cart-line]") || productForm || input.parentElement;
    setMessage(scope, productForm ? "Only " + max + " more currently available." : "Only " + max + " currently available. The quantity was adjusted.");
    return true;
  }

  function initializeCartInputs() {
    document.querySelectorAll("[data-inventory-cart-input]").forEach(function (input) {
      if (!clampCartInput(input)) return;
      var form = input.closest("[data-inventory-cart-form]");
      var status = form?.querySelector("[data-inventory-cart-status]");
      if (status) status.textContent = "Inventory changed. Review the adjusted quantity, then update the cart or continue to checkout.";
    });
  }

  async function reconcileAuthoritativeCart(form, message) {
    try {
      var cartUrl = window.routes?.cart_url ? window.routes.cart_url + ".js" : "/cart.js";
      var response = await fetch(cartUrl, { headers: { Accept: "application/json" } });
      var cart = await response.json();
      var byKey = new Map((cart.items || []).map(function (item) { return [String(item.key), item.quantity]; }));
      form.querySelectorAll("[data-inventory-cart-input]").forEach(function (input) {
        var quantity = byKey.get(String(input.dataset.cartLineKey));
        input.value = String(quantity == null ? 0 : quantity);
        input.dataset.inventoryOriginal = input.value;
      });
      document.dispatchEvent(new CustomEvent("darkstone:refresh-header-cart"));
    } catch (error) {
      console.error("[Darkstone inventory] Cart reconciliation failed.", error);
    }
    var status = form.querySelector("[data-inventory-cart-status]");
    if (status) status.textContent = message || "Inventory changed. Cart quantities were refreshed.";
  }

  async function guardCartSubmit(event, form) {
    form.querySelectorAll("[data-inventory-cart-input]").forEach(clampCartInput);
    var changed = Array.from(form.querySelectorAll("[data-inventory-cart-input]")).filter(function (input) {
      return number(input.value, 0) !== number(input.dataset.inventoryOriginal, 0);
    });
    if (!changed.length) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    var buttons = Array.from(form.querySelectorAll('button[type="submit"]'));
    buttons.forEach(function (button) { button.disabled = true; });
    var status = form.querySelector("[data-inventory-cart-status]");
    if (status) status.textContent = "Updating current inventory…";

    try {
      for (var input of changed) {
        var response = await fetch(window.routes?.cart_change_url || "/cart/change.js", {
          method: "POST",
          credentials: "same-origin",
          headers: { Accept: "application/json", "Content-Type": "application/json" },
          body: JSON.stringify({ id: input.dataset.cartLineKey, quantity: Math.max(0, number(input.value, 0)) }),
        });
        var payload = await response.json().catch(function () { return null; });
        if (!response.ok) {
          var failure = new Error(payload?.description || payload?.message || "Inventory changed while updating your cart.");
          failure.isInventoryError = response.status === 422;
          throw failure;
        }
      }
      if (event.submitter?.name === "checkout") {
        window.location.href = "/checkout";
      } else {
        window.location.reload();
      }
    } catch (error) {
      await reconcileAuthoritativeCart(form, error.message || "Inventory changed. Cart quantities were refreshed.");
      buttons.forEach(function (button) { button.disabled = false; });
    }
  }

  document.addEventListener("input", function (event) {
    if (event.target instanceof HTMLInputElement && event.target.matches("[data-inventory-cart-input], [data-inventory-quantity-input]")) {
      clampCartInput(event.target);
    }
  });

  document.addEventListener("click", function (event) {
    var button = event.target.closest?.("[data-inventory-v1-quick-add]");
    if (button instanceof HTMLButtonElement) submitV1QuickAdd(event, button);
  }, true);

  document.addEventListener("submit", function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    if (form.matches("[data-inventory-quick-add-form]")) {
      if (form.matches("[data-pulse-quick-add-form]")) {
        guardQuickAdd(event, form);
      } else {
        submitStandaloneQuickAdd(event, form);
      }
      return;
    }

    if (form.matches("[data-inventory-cart-form]")) {
      guardCartSubmit(event, form);
      return;
    }

    if (form.matches("[data-inventory-product-form]")) {
      submitProductForm(event, form);
    }
  }, true);

  document.addEventListener("darkstone:inventory-error", function (event) {
    var form = event.detail && event.detail.form;
    if (form instanceof HTMLFormElement) {
      setMessage(form, event.detail.message || "Inventory changed. Refresh and try again.");
    }
  });

  document.addEventListener("darkstone:cart-updated", function (event) {
    var id = variantId(event.detail && event.detail.variantId);
    var quantityDelta = Math.max(0, number(event.detail && event.detail.quantityDelta, 0));
    if (!id || !quantityDelta) return;

    document.querySelectorAll("[data-inventory-quick-add-form]").forEach(function (form) {
      if (variantId(form.querySelector('input[name="id"]')?.value) !== id || !form.hasAttribute("data-inventory-cart-quantity")) return;
      form.dataset.inventoryCartQuantity = String(number(form.dataset.inventoryCartQuantity, 0) + quantityDelta);
      renderQuickAdd(form);
    });

    document.querySelectorAll("[data-inventory-v1-quick-add]").forEach(function (button) {
      if (variantId(button.dataset.productId) !== id || !button.hasAttribute("data-inventory-cart-quantity")) return;
      button.dataset.inventoryCartQuantity = String(number(button.dataset.inventoryCartQuantity, 0) + quantityDelta);
      renderV1QuickAdd(button);
    });

    var record = productInventory.get(id);
    if (record) {
      record.cartQuantity = number(record.cartQuantity, 0) + quantityDelta;
      if (activeVariantId === id) applyProductVariant(record);
    }
  });

  window.DarkstoneInventoryGuard = { updateProductVariant: updateProductVariant };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      parseProductInventory();
      initializeCartInputs();
      document.querySelectorAll("[data-inventory-quick-add-form]").forEach(renderQuickAdd);
      document.querySelectorAll("[data-inventory-v1-quick-add]").forEach(renderV1QuickAdd);
    }, { once: true });
  } else {
    parseProductInventory();
    initializeCartInputs();
    document.querySelectorAll("[data-inventory-quick-add-form]").forEach(renderQuickAdd);
    document.querySelectorAll("[data-inventory-v1-quick-add]").forEach(renderV1QuickAdd);
  }
})();
